import React from 'react';
import { Button, Tooltip } from 'antd';
import { ArrowRightOutlined, CheckCircleOutlined, EyeOutlined, FileTextOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { OrderStepBar, OrderStepPrimaryAction } from './orderStepBar';
import type { OrderProcessOwner, OrderProcessStepKey } from './orderProcessSteps';

interface OrderStepActionBarProps {
  bar: OrderStepBar;
  /** The viewed step is a finished one: the bar only offers the way back. */
  previewOf: OrderProcessStepKey | null;
  onBackToCurrent: () => void;
  onPrimary: (action: OrderStepPrimaryAction) => void;
  busy?: boolean;
  /** „Zmień status ręcznie” — the existing status dialog, the way around the guide. */
  manualStatus?: React.ReactNode;
  /** Step actions before the main one (Szkic: „Zapisz szkic”). */
  secondary?: React.ReactNode;
  owner: OrderProcessOwner;
}

/**
 * Sticky bar under the ZK card (makieta `bar()`): hint on the left — a
 * warning never disables the action (decision 2026-10-04 pkt 5) — and the
 * main action of the step on the right.
 */
export default function OrderStepActionBar({
  bar,
  previewOf,
  onBackToCurrent,
  onPrimary,
  busy = false,
  manualStatus,
  secondary,
  owner,
}: OrderStepActionBarProps) {
  const { t } = useTranslation(['orders']);
  if (previewOf) {
    return (
      <div className="zk-bar" data-testid="zk-action-bar" role="region" aria-label={t('orders:process.bar.ariaLabel')}>
        <span className="zk-bar__hint"><EyeOutlined aria-hidden="true" />
          {t('orders:process.bar.preview', { step: t(`orders:process.steps.${previewOf}.label`) })}
        </span>
        <div className="zk-bar__actions">
          <Button type="primary" onClick={onBackToCurrent} icon={<ArrowRightOutlined />} iconPosition="end">
            {t('orders:process.bar.backToCurrent')}
          </Button>
        </div>
      </div>
    );
  }
  const warning = bar.hint.tone === 'warning';
  const hint = bar.primary || bar.closed || bar.manualOnly
    ? t(bar.hint.key, bar.hint.params)
    : `${t(bar.hint.key, bar.hint.params)} ${t('orders:process.bar.noAction', { owner: t(`orders:process.owner.${owner}`) })}`;
  return (
    <div
      className={`zk-bar${bar.primaryWarns ? ' zk-bar--warning' : ''}`}
      data-testid="zk-action-bar"
      data-warns={bar.primaryWarns ? 'true' : undefined}
      role="region"
      aria-label={t('orders:process.bar.ariaLabel')}
    >
      <span className={`zk-bar__hint${warning ? ' zk-bar__hint--warning' : ''}`} data-testid="zk-action-hint" data-tone={bar.hint.tone}>
        {warning ? <WarningOutlined aria-hidden="true" /> : <FileTextOutlined aria-hidden="true" />}
        {hint}
      </span>
      <div className="zk-bar__actions">
        {manualStatus}
        {secondary}
        {bar.secondary && (
          <Button
            disabled={busy}
            onClick={() => bar.secondary && onPrimary(bar.secondary)}
            data-testid="zk-secondary-action"
          >
            {t(bar.secondary.labelKey)}
          </Button>
        )}
        {bar.closed && (
          <span className="zk-bar__done"><CheckCircleOutlined aria-hidden="true" />{t('orders:process.bar.invoicedBadge')}</span>
        )}
        {bar.primary && (
          <Tooltip title={bar.primaryDisabled ? hint : undefined}>
            <Button
              type="primary"
              loading={busy}
              disabled={bar.primaryDisabled}
              onClick={() => bar.primary && onPrimary(bar.primary)}
              icon={bar.primaryWarns ? <WarningOutlined /> : <ArrowRightOutlined />}
              iconPosition={bar.primaryWarns ? 'start' : 'end'}
              data-testid="zk-primary-action"
            >
              {t(bar.primary.labelKey)}
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
